import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSend, FiMessageCircle, FiX, FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { chatAPI } from '../../services/api';

const ChatInterface = ({ studentID, selectedClusters, district, inline = false, onBack, chatSession, onUpdateChatSession }) => {
  // Initialise from persisted session when available (survives navigate-back)
  const [messages, setMessages] = useState(chatSession?.messages || []);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState(chatSession?.chatId || null);
  const [sessionId, setSessionId] = useState(chatSession?.sessionId || null);
  const [isOpen, setIsOpen] = useState(inline);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState('');
  const messagesContainerRef = useRef(null);

  // Sync a partial update to both local state and the parent hook
  const syncSession = useCallback((update) => {
    if (update.messages !== undefined) setMessages(update.messages);
    if (update.chatId   !== undefined) setChatId(update.chatId);
    if (update.sessionId !== undefined) setSessionId(update.sessionId);
    if (onUpdateChatSession) onUpdateChatSession(update);
  }, [onUpdateChatSession]);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-initialize chat in inline mode — skip if session already exists
  useEffect(() => {
    if (inline && !chatSession?.initialized && !chatId && !initializing) {
      initializeChat();
    }
  }, [inline]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeChat = async () => {
    try {
      setInitializing(true);
      setError('');

      console.log('[ChatInterface] Initializing chat:', { studentID, district, clustersCount: selectedClusters?.length });
      const response = await chatAPI.initChat(studentID, selectedClusters, district);
      console.log('[ChatInterface] Chat API response:', response);

      if (response.success) {
        console.log('[ChatInterface] Chat initialized successfully');
        const initMessages = [{ type: 'bot', text: response.data.message, timestamp: new Date() }];
        syncSession({
          chatId: response.data.chatId,
          sessionId: response.data.sessionId,
          messages: initMessages,
          initialized: true
        });
      } else {
        console.log('[ChatInterface] Chat init failed:', response.error);
        setError(response.error || 'Failed to initialize chat');
      }
    } catch (err) {
      console.error('[ChatInterface] Error initializing chat:', err);
      console.error('[ChatInterface] Error details:', err.response?.data, err.message);
      setError('Failed to start chat. Chat may not be configured for this district.');
    } finally {
      setInitializing(false);
    }
  };

  const resetSession = () => {
    if (!window.confirm('Start a new conversation? This will clear the current chat history.')) return;
    setMessages([]);
    setChatId(null);
    setSessionId(null);
    setError('');
    if (onUpdateChatSession) {
      onUpdateChatSession({ chatId: null, sessionId: null, messages: [], initialized: false });
    }
    initializeChat();
  };

  const handleOpenChat = () => {
    setIsOpen(true);
    if (!chatId && !initializing) {
      initializeChat();
    }
  };

  const handleCloseChat = () => {
    setIsOpen(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim() || !chatId || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    console.log('[ChatInterface] Sending message with sessionId:', sessionId);

    // Optimistically add user message
    const withUser = [...messages, { type: 'user', text: userMessage, timestamp: new Date() }];
    syncSession({ messages: withUser });

    setLoading(true);

    try {
      const response = await chatAPI.sendMessage(userMessage, chatId, sessionId);
      console.log('[ChatInterface] Response:', response);

      if (response.success) {
        const updatedSessionId = response.data.sessionId || sessionId;
        const withBot = [...withUser, { type: 'bot', text: response.data.message, timestamp: new Date() }];
        syncSession({ messages: withBot, sessionId: updatedSessionId });
      } else {
        const withError = [...withUser, { type: 'error', text: 'Sorry, I encountered an error. Please try again.', timestamp: new Date() }];
        syncSession({ messages: withError });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const withError = [...withUser, { type: 'error', text: 'Failed to send message. Please try again.', timestamp: new Date() }];
      syncSession({ messages: withError });
    } finally {
      setLoading(false);
    }
  };

  // Inline mode: full-page chat (own survey stage)
  if (inline) {
    return (
      <div className="flex flex-col bg-white rounded-lg shadow-lg border border-gray-200" style={{ height: 'calc(100vh - 180px)', minHeight: '500px' }}>
        {/* Header */}
        <div className="flex-shrink-0 bg-primary-600 text-white px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center min-w-0">
              <FiMessageCircle className="h-6 w-6 mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-lg">Pathways Advisor</h3>
                <p className="text-sm text-primary-100 truncate">Ask about courses, certifications, and career opportunities</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={resetSession}
                disabled={initializing}
                title="Start a new conversation"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white bg-opacity-15 hover:bg-opacity-25 transition-colors disabled:opacity-50"
              >
                <FiRefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">New Chat</span>
              </button>
              {onBack && (
                <button
                  onClick={onBack}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-white text-primary-700 hover:bg-primary-50 transition-colors"
                >
                  <FiArrowLeft className="h-4 w-4" />
                  Back to Results
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Messages — scrolls internally, never affects page scroll */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
          {initializing && (
            <div className="flex justify-center items-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Connecting to advisor...</p>
              </div>
            </div>
          )}

          {error && !initializing && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  message.type === 'user'
                    ? 'bg-primary-600 text-white'
                    : message.type === 'error'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}
              >
                {message.type === 'user' ? (
                  <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                ) : (
                  <div className="text-sm">
                    <ReactMarkdown
                      components={{
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-3 mb-2" {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h3: ({ node, ...props }) => <h3 className="text-base font-semibold mt-3 mb-1" {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h4: ({ node, ...props }) => <h4 className="text-sm font-semibold mt-2 mb-1" {...props} />,
                        p: ({ node, ...props }) => <p className="my-2" {...props} />,
                        ul: ({ node, ...props }) => <ul className="list-disc list-inside my-2 space-y-1" {...props} />,
                        ol: ({ node, ...props }) => <ol className="list-decimal list-inside my-2 space-y-1" {...props} />,
                        li: ({ node, ...props }) => <li className="ml-2" {...props} />,
                        strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                        em: ({ node, ...props }) => <em className="italic" {...props} />,
                        hr: ({ node, ...props }) => <hr className="my-4 border-gray-300" {...props} />,
                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                        a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
                        table: ({ node, ...props }) => (
                          <div className="overflow-x-auto my-3">
                            <table className="min-w-full border-collapse border border-gray-300" {...props} />
                          </div>
                        ),
                        thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
                        tbody: ({ node, ...props }) => <tbody {...props} />,
                        tr: ({ node, ...props }) => <tr className="border-b border-gray-300" {...props} />,
                        th: ({ node, ...props }) => (
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold" {...props} />
                        ),
                        td: ({ node, ...props }) => (
                          <td className="border border-gray-300 px-3 py-2" {...props} />
                        ),
                        code: ({ node, inline, ...props }) =>
                          inline ? (
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-xs" {...props} />
                          ) : (
                            <code className="block bg-gray-100 p-2 rounded my-2 text-xs" {...props} />
                          )
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  </div>
                )}
                <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-primary-200' : 'text-gray-500'}`}>
                  {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-white text-gray-900 border border-gray-200 rounded-lg px-4 py-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input — pinned to bottom */}
        <form onSubmit={handleSendMessage} className="flex-shrink-0 p-4 border-t border-gray-200 bg-white rounded-b-lg">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={loading || !chatId || initializing}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={loading || !inputMessage.trim() || !chatId || initializing}
              className="bg-primary-600 text-white p-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiSend className="h-5 w-5" />
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Floating mode: chat bubble button
  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={handleOpenChat}
          className="fixed bottom-6 right-6 bg-primary-600 text-white rounded-full p-4 shadow-lg hover:bg-primary-700 transition-all transform hover:scale-110 z-50"
          title="Chat with pathways advisor"
        >
          <FiMessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-primary-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center">
              <FiMessageCircle className="h-5 w-5 mr-2" />
              <h3 className="font-semibold">Pathways Advisor</h3>
            </div>
            <button
              onClick={handleCloseChat}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {initializing && (
              <div className="flex justify-center items-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Connecting to advisor...</p>
                </div>
              </div>
            )}

            {error && !initializing && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                {error}
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                    message.type === 'user'
                      ? 'bg-primary-600 text-white'
                      : message.type === 'error'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                  {message.type === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  ) : (
                    <div className="text-sm">
                      <ReactMarkdown
                        components={{
                          // eslint-disable-next-line jsx-a11y/heading-has-content
                          h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
                          // eslint-disable-next-line jsx-a11y/heading-has-content
                          h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-3 mb-2" {...props} />,
                          // eslint-disable-next-line jsx-a11y/heading-has-content
                          h3: ({ node, ...props }) => <h3 className="text-base font-semibold mt-3 mb-1" {...props} />,
                          // eslint-disable-next-line jsx-a11y/heading-has-content
                          h4: ({ node, ...props }) => <h4 className="text-sm font-semibold mt-2 mb-1" {...props} />,
                          p: ({ node, ...props }) => <p className="my-2" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc list-inside my-2 space-y-1" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal list-inside my-2 space-y-1" {...props} />,
                          li: ({ node, ...props }) => <li className="ml-2" {...props} />,
                          strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                          em: ({ node, ...props }) => <em className="italic" {...props} />,
                          hr: ({ node, ...props }) => <hr className="my-4 border-gray-300" {...props} />,
                          // eslint-disable-next-line jsx-a11y/anchor-has-content
                          a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
                          table: ({ node, ...props }) => (
                            <div className="overflow-x-auto my-3">
                              <table className="min-w-full border-collapse border border-gray-300" {...props} />
                            </div>
                          ),
                          thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
                          tbody: ({ node, ...props }) => <tbody {...props} />,
                          tr: ({ node, ...props }) => <tr className="border-b border-gray-300" {...props} />,
                          th: ({ node, ...props }) => (
                            <th className="border border-gray-300 px-3 py-2 text-left font-semibold" {...props} />
                          ),
                          td: ({ node, ...props }) => (
                            <td className="border border-gray-300 px-3 py-2" {...props} />
                          ),
                          code: ({ node, inline, ...props }) =>
                            inline ? (
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs" {...props} />
                            ) : (
                              <code className="block bg-gray-100 p-2 rounded my-2 text-xs" {...props} />
                            )
                        }}
                      >
                        {message.text}
                      </ReactMarkdown>
                    </div>
                  )}
                  <p
                    className={`text-xs mt-1 ${
                      message.type === 'user' ? 'text-primary-200' : 'text-gray-500'
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-900 border border-gray-200 rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.1s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                disabled={loading || !chatId || initializing}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={loading || !inputMessage.trim() || !chatId || initializing}
                className="bg-primary-600 text-white p-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiSend className="h-5 w-5" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatInterface;
